import type { AxiosError } from 'axios';
import { apiClient, apiGet, refusalWords } from './client';
import type { OperationResult } from './types';
import type { ScenarioChangePart, ScenarioCheck } from './scenarioVersions';

// ─────────────────────────────────────────────────────────────────────────────
// Scenario Intelligence model roles (plan §6, D-SI-9) — vNEXT api/ScenarioIntelligence.
// Scenario, Critic and Evaluator models are settings that name a registered deployment; the agent under test is not a
// role here (it always runs on its own configured model, D-SI-4). The server decides eligibility, order and every word;
// this module only carries them. Keys arrive camelized from PascalCase, so AIModelId reads as `aIModelId`.
// ─────────────────────────────────────────────────────────────────────────────

/** Matches OO.Domain.AIInfo.EvaluationModelRole (sent as a number). */
export enum EvaluationModelRole {
  Scenario = 1,
  Critic = 2,
  Evaluator = 3,
}

/** One registered deployment as a picker row. All rates null = Price unavailable, never $0. */
export interface EvaluationModelOption {
  aIModelId: number;
  name: string;
  title?: string | null;
  deployment?: string | null;
  apiId: number;
  apiTitle?: string | null;
  apiHost?: string | null;
  provider?: string | null;
  /** null = platform-wide row. */
  merchantId?: number | null;
  usable: boolean;
  problems: string[];
  inputPer1MTokens?: number | null;
  cachedInputPer1MTokens?: number | null;
  outputPer1MTokens?: number | null;
  priced: boolean;
  /** true/false from the Foundry project listing; null = not checked. */
  inFoundryProject?: boolean | null;
  supportsTemperature: boolean;
  supportsReasoningEffort: boolean;
  maxContextTokens?: number | null;
  toolsSupport: string;
  structuredOutputSupport: string;
  retirementDateUtc?: string | null;
  retirementDaysUntil?: number | null;
  /** Evaluator only: "Qualifying" | "Not measured". */
  qualification?: string | null;
  isCurrent: boolean;
}

export interface EvaluationModelOptions {
  role: EvaluationModelRole;
  roleLabel: string;
  rule: string;
  merchantId: number;
  checkedAtUtc: string;
  availabilityNote?: string | null;
  usable: EvaluationModelOption[];
  notUsable: EvaluationModelOption[];
}

export interface EvaluationModelRoleView {
  role: EvaluationModelRole;
  label: string;
  rule: string;
  mayBeOff: boolean;
  apiId?: number | null;
  deployment?: string | null;
  row?: EvaluationModelOption | null;
  /** Why the role cannot run in this practice now; null when it can (or is off). */
  problem?: string | null;
  qualification?: string | null;
}

export interface EvaluationModelPlanView {
  /** 0 = no plan row yet (scenario-intelligence sql/002 not run). */
  version: number;
  changedAtUtc?: string | null;
  changedByUserId?: number | null;
  note?: string | null;
  merchantId: number;
  roles: EvaluationModelRoleView[];
}

/** The model that answered a call (vNEXT AppliedModel). */
export interface AppliedModel {
  aIModelId: number;
  modelName: string;
  deploymentName?: string | null;
  apiId?: number | null;
  apiTitle?: string | null;
  source?: string | null;
}

/** A failed call, carrying the server's own words. */
export class ScenarioIntelligenceApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'ScenarioIntelligenceApiError';
  }
}

const BASE = '/api/ScenarioIntelligence';

export const getModelPlan = (merchantId?: number | null): Promise<EvaluationModelPlanView> =>
  read<EvaluationModelPlanView>(`${BASE}/ModelPlan`, merchantId ? { merchantId } : undefined);

export const getRoleModels = (role: EvaluationModelRole, merchantId?: number | null): Promise<EvaluationModelOptions> =>
  read<EvaluationModelOptions>(`${BASE}/Models`, merchantId ? { role, merchantId } : { role });

/** Choose the deployment a role runs on (null switches the Critic off). Returns the new plan; refusals carry the reason. */
export async function setRoleModel(role: EvaluationModelRole, aiModelId: number | null, merchantId?: number | null): Promise<EvaluationModelPlanView> {
  let res: OperationResult<EvaluationModelPlanView>;
  try {
    ({ data: res } = await apiClient.post<OperationResult<EvaluationModelPlanView>>(`${BASE}/ModelPlan/${role}`, {
      aiModelId,
      merchantId: merchantId ?? undefined,
    }));
  } catch (err) {
    throw new ScenarioIntelligenceApiError(refusalWords(err), (err as AxiosError)?.response?.status);
  }
  if (!res.isSuccess || !res.data) throw new ScenarioIntelligenceApiError(res.message || 'The server refused the choice.');
  return res.data;
}

/** "$0.10 / $0.50", or null when the row has no rate (the caller shows Price unavailable). */
export function formatRates(o: Pick<EvaluationModelOption, 'inputPer1MTokens' | 'outputPer1MTokens' | 'priced'>): string | null {
  if (!o.priced || o.inputPer1MTokens == null || o.outputPer1MTokens == null) return null;
  const f = (n: number) => `$${n.toFixed(2)}`;
  return `${f(o.inputPer1MTokens)} / ${f(o.outputPer1MTokens)}`;
}

async function read<T>(url: string, params?: unknown): Promise<T> {
  let res: OperationResult<T>;
  try {
    res = await apiGet<OperationResult<T>>(url, params);
  } catch (err) {
    throw new ScenarioIntelligenceApiError(refusalWords(err), (err as AxiosError)?.response?.status);
  }
  if (!res.isSuccess) throw new ScenarioIntelligenceApiError(res.message || 'The server could not answer.');
  return res.data as T;
}


// ─────────────────────────────────────────────────────────────────────────────
// SI-05a — generation jobs and their proposals (vNEXT api/ScenarioIntelligence GenerationJobs / Proposals).
// A generation is a durable job (its key); its proposals are drafts (aivalidation.ScenarioDraft). Nothing joins the library
// until a person adds it, and adding never approves. The server decides every state, gap and word.
// ─────────────────────────────────────────────────────────────────────────────

export interface ProposalCardExpectation {
  text: string;
  /** "Operational" | "Clinical". */
  kind: string;
  pendingSmeReview: boolean;
  checks: string[];
}

export interface ProposalCardSource {
  kind: string;
  ref: string;
  note?: string | null;
  /** False when the reference is not something the agent is configured with: shown, never counted. */
  verified: boolean;
}

export interface ProposalCardGap {
  kind: string;
  text: string;
  action: string;
}

/** The seven fields of mockup 03 plus paraphrases, gaps and the rules' problems. */
export interface ProposalCard {
  family: string;
  title: string;
  situation: string;
  turns: string[];
  whyItMatters: string;
  expectedBehavior: ProposalCardExpectation[];
  evidenceNeeded: string[];
  sources: ProposalCardSource[];
  requirement?: string | null;
  distinct: string;
  paraphrases: string[];
  gaps: ProposalCardGap[];
  problems: string[];
  possibleDuplicateOf?: string | null;
}

export type ProposalState = 'Proposed' | 'Added' | 'Rejected';
export type ProposalReadiness = 'Runnable' | 'Incomplete';

export interface ScenarioProposal {
  draftId: number;
  scenarioId: string;
  generationKey: string;
  state: ProposalState;
  readiness: ProposalReadiness;
  card: ProposalCard | null;
  modelName?: string | null;
  aIModelId?: number | null;
  concern?: string | null;
  createdAtUtc: string;
  requestedBy?: number | null;
  addedSnapshotId?: number | null;
  decidedAtUtc?: string | null;
  decidedBy?: number | null;
  batchEligible: boolean;
  batchExclusion?: string | null;
}

export interface GenerationStage {
  key: string;
  title: string;
  /** Done | Running | Pending | Failed | Skipped | Later. */
  status: string;
  detail: string;
}

/** What the calls under a generation's trace cost. costUsd is null when any call is unpriced; pricedUsd is the priced part. */
export interface GenerationCost {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd?: number | null;
  unpricedCalls: number;
  pricedUsd?: number | null;
}

export type GenerationState = 'Queued' | 'Running' | 'Completed' | 'Failed';

export interface ScenarioGeneration {
  key: string;
  state: GenerationState;
  stateReason?: string | null;
  agentId: number;
  agentName?: string | null;
  agentCanonicalName?: string | null;
  merchantId: number;
  concern: string;
  maxProposals: number;
  scenarioModel?: AppliedModel | null;
  requestedBy?: number | null;
  requestedAtUtc: string;
  finishedAtUtc?: string | null;
  stages: GenerationStage[];
  proposalCount: number;
  familyCount: number;
  paraphrasesGrouped: number;
  runnableCount: number;
  incompleteCount: number;
  failedAttempts: number;
  failedAttemptReasons: string[];
  uncovered: string[];
  cost?: GenerationCost | null;
  proposals: ScenarioProposal[];
}

export interface GenerationSubmission {
  key: string;
  started: boolean;
  replayed: boolean;
  generation: ScenarioGeneration;
}

export interface StartGenerationRequest {
  agentId: number;
  concern: string;
  /** For this generation only; null = the plan's Scenario model. Never saved as the setting. */
  scenarioModelId?: number | null;
  maxProposals?: number | null;
  idempotencyKey: string;
  merchantId?: number | null;
}

export type ProposalDecision = 'Add' | 'Reject';

export interface ProposalDecisionOutcome {
  draftId: number;
  /** Added | Rejected | Unchanged | Refused. */
  outcome: string;
  reason?: string | null;
  scenarioId?: string | null;
  snapshotId?: number | null;
}

/** The generation's states that need no more polling. */
export const isGenerationFinished = (g: Pick<ScenarioGeneration, 'state'> | null | undefined) =>
  g?.state === 'Completed' || g?.state === 'Failed';

/** Start a generation. Refusals (a bad request, a model that cannot run, a reused request id) carry the server's words. */
export const startGeneration = (req: StartGenerationRequest): Promise<GenerationSubmission> =>
  write<GenerationSubmission>(`${BASE}/GenerationJobs`, req);

export const getGeneration = (key: string, merchantId?: number | null): Promise<ScenarioGeneration> =>
  read<ScenarioGeneration>(`${BASE}/GenerationJobs/${encodeURIComponent(key)}`, merchantId ? { merchantId } : undefined);

/** The agent's latest generations in the practice, newest first, each with its proposals. */
export const listProposals = (agentId: number, merchantId?: number | null, take = 5): Promise<ScenarioGeneration[]> =>
  read<ScenarioGeneration[]>(`${BASE}/Proposals`, merchantId ? { agentId, merchantId, take } : { agentId, take }).then((r) => r ?? []);

/** Add to the library as drafts (never approved), or reject. One outcome per proposal. */
export const decideProposals = (
  draftIds: number[],
  decision: ProposalDecision,
  opts: { batch?: boolean; reason?: string; merchantId?: number | null } = {},
): Promise<ProposalDecisionOutcome[]> =>
  write<ProposalDecisionOutcome[]>(`${BASE}/Proposals/Decide`, {
    draftIds,
    decision,
    batch: opts.batch ?? false,
    reason: opts.reason,
    merchantId: opts.merchantId ?? undefined,
  }).then((r) => r ?? []);

/** "Recorded priced usage: $0.02, plus 1 unpriced call" — never an unpriced call summed as $0. */
export function describeCost(cost: GenerationCost | null | undefined): string | null {
  if (!cost || cost.calls === 0) return null;
  const priced = cost.pricedUsd != null ? `$${cost.pricedUsd < 0.01 ? cost.pricedUsd.toFixed(4) : cost.pricedUsd.toFixed(2)}` : null;
  const unpriced = cost.unpricedCalls > 0 ? `${cost.unpricedCalls} unpriced call${cost.unpricedCalls === 1 ? '' : 's'}` : null;
  if (priced && unpriced) return `Recorded priced usage: ${priced}, plus ${unpriced}`;
  if (priced) return `Recorded priced usage: ${priced}`;
  return `Price unavailable: ${unpriced}`;
}

async function write<T>(url: string, body: unknown): Promise<T> {
  let res: OperationResult<T>;
  try {
    ({ data: res } = await apiClient.post<OperationResult<T>>(url, body));
  } catch (err) {
    throw new ScenarioIntelligenceApiError(refusalWords(err), (err as AxiosError)?.response?.status);
  }
  if (!res.isSuccess) throw new ScenarioIntelligenceApiError(res.message || 'The server refused the request.');
  return res.data as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Screen 04 — improving ONE scenario with an AI action (vNEXT api/ScenarioIntelligence/Improvements).
// The Scenario model proposes lines; the person keeps or drops each; the server assesses the version the kept lines make
// (its controls replayed on stored text, its compile check, what a save does to its approval). Nothing is saved here:
// saving is the editor's own save (api/Scenarios/Version), with its 409 and three-way merge. The proposal travels as
// canonical JSON text (proposalJson) and goes back to Assess exactly as it came, so no casing renames a check's keys.
// ─────────────────────────────────────────────────────────────────────────────

export type ImprovementAction = 'StrengthenChecks' | 'CheckExpectedBehavior' | 'MoreRealistic' | 'SimplifyWording' | 'AskForChange';

/** The in-place actions, in the order the drawer offers them; the server owns what each may change. */
export const IMPROVEMENT_ACTIONS: Array<{ action: ImprovementAction; label: string; changes: string }> = [
  { action: 'StrengthenChecks', label: 'Strengthen the checks', changes: 'Changes checks only, so a wrong reply fails and a right one still passes.' },
  { action: 'CheckExpectedBehavior', label: 'Check the expected behavior', changes: 'May change the checks and the purpose, where they do not say the same thing.' },
  { action: 'MoreRealistic', label: 'Make this more realistic', changes: 'Changes only what the user says, keeping every fact the checks depend on.' },
  { action: 'SimplifyWording', label: 'Simplify wording', changes: 'Changes only the purpose. Wording only: it can be approved in a batch.' },
];

export type ImprovementLineKind = 'AddCheck' | 'RemoveCheck' | 'ReplaceCheck' | 'UserWords' | 'Purpose';

export interface ImprovementLine {
  id: string;
  kind: ImprovementLineKind;
  /** 0-based turn the line is about; null for the purpose. */
  turnIndex?: number | null;
  why: string;
  beforeCheck?: ScenarioCheck | null;
  afterChecks?: ScenarioCheck[] | null;
  before?: string | null;
  after?: string | null;
  strength?: string | null;
}

export interface ImprovementToolCall {
  tool: string;
  args: Record<string, string>;
}

export interface ImprovementControl {
  turnIndex: number;
  reply: string;
  toolCalls: ImprovementToolCall[];
}

export interface ImprovementProposal {
  action: ImprovementAction;
  actionLabel: string;
  why: string;
  lines: ImprovementLine[];
  leftOut: string[];
  wrongReply?: ImprovementControl | null;
  safeParaphrase?: ImprovementControl | null;
}

export type ControlState = 'AsIntended' | 'NotAsIntended' | 'NotGiven' | 'NotNeeded';

export interface ControlOutcome {
  name: 'Negative' | 'SafeParaphrase';
  state: ControlState;
  words: string;
}

export interface ApprovalEffect {
  wasApproved: boolean;
  words: string;
  policy: 'Material' | 'WordingOnly';
  approvedByName?: string | null;
  approvedByRole?: string | null;
  approvedAtUtc?: string | null;
  approvedHash?: string | null;
}

export interface ImprovementAssessment {
  /** The version the kept lines make, as canonical JSON text; the editor's save takes it as it is. */
  versionJson: string;
  hash: string;
  changedParts: ScenarioChangePart[];
  material: boolean;
  /** Every control behaved as intended, or none was needed. An unproven change is never saved in one click. */
  proven: boolean;
  controls: ControlOutcome[];
  compileProblems: string[];
  approval: ApprovalEffect;
  keptLines: number;
  baseVersionNumber?: number | null;
  nextVersionNumber?: number | null;
}

export interface ScenarioImprovementView {
  scenarioId: string;
  agentId: number;
  baseHash: string;
  originalJson: string;
  /** The proposal as canonical JSON text: read it with parseProposal, and send it back to Assess unchanged. */
  proposalJson: string;
  assessment: ImprovementAssessment;
  answeredBy?: AppliedModel | null;
  cost?: GenerationCost | null;
  attempts: number;
  proposedAtUtc: string;
}

export interface ProposeImprovementRequest {
  agentId: number;
  scenarioId: string;
  baseHash: string;
  action: ImprovementAction | 'AskForChange';
  request?: string;
  /** For this proposal only; null = the plan's Scenario model. Never saved as the setting. */
  scenarioModelId?: number | null;
  merchantId?: number | null;
}

export const proposeImprovement = (req: ProposeImprovementRequest): Promise<ScenarioImprovementView> =>
  write<ScenarioImprovementView>(`${BASE}/Improvements`, { ...req, merchantId: req.merchantId ?? undefined });

export const assessImprovement = (req: {
  agentId: number;
  scenarioId: string;
  baseHash: string;
  proposalJson: string;
  keptLineIds: string[];
  merchantId?: number | null;
}): Promise<ImprovementAssessment> =>
  write<ImprovementAssessment>(`${BASE}/Improvements/Assess`, { ...req, merchantId: req.merchantId ?? undefined });

/** The proposal from its canonical JSON text; null when the text is not one. */
export function parseProposal(json: string | null | undefined): ImprovementProposal | null {
  if (!json) return null;
  try {
    const p = JSON.parse(json) as ImprovementProposal;
    return { ...p, lines: p.lines ?? [], leftOut: p.leftOut ?? [] };
  } catch {
    return null;
  }
}
